import type { ReactNode } from 'react';
import { useCurrentRole } from '@/hooks/use-current-role';

export interface RoleGateProps {
  role: 'admin' | 'member';
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * RoleGate — binary render gate based on workspace role.
 *
 * - `role="admin"` → renders children only when current role is admin
 * - `role="member"` → renders children when role is admin OR member (admin is a superset)
 * - While loading or missing/invalid token → renders fallback (least privilege)
 * - Fallback defaults to null
 */
export function RoleGate({ role, fallback = null, children }: RoleGateProps) {
  const { role: currentRole, isLoading } = useCurrentRole();

  if (isLoading) return <>{fallback}</>;

  const matches =
    role === 'admin'
      ? currentRole === 'admin'
      : currentRole === 'admin' || currentRole === 'member';

  return <>{matches ? children : fallback}</>;
}
